/**
 * Buy N, get N — the shape the shop reads, and how it is worded.
 *
 * One formatter, used by the product card, the cart, the checkout summary and
 * the bill. Each of those had its own sentence once and they disagreed about
 * whether 0.5 kg was "0.5 kg" or "500 g", which is the sort of difference a
 * customer reads as two different offers.
 */

export interface ProductOffer {
  id: number;
  label: string;
  buy_product_id: number;
  buy_qty: number;
  buy_unit: 'kg' | 'items';
  get_product_id: number;
  get_qty: number;
  get_price: number;
  max_repeats: number;
  /** The reward is the same pack the customer bought, not a weight. */
  get_matches_variant?: number | boolean;
  buy_name: string;
  get_name: string;
  get_unit: string;
  get_emoji?: string;
}

/**
 * Sub-kilo amounts read in grams.
 *
 * "0.5 kg free" looks like a rounding error beside a price; "500 g free" reads
 * like food. The same rule the cart already applies to gifts — asked for once,
 * so it holds everywhere rather than in the one place it was asked about.
 */
/**
 * A plain count, for the quantities that are not weights.
 *
 * mysql2 returns DECIMAL as a string, so buy_qty arrives as "1.000" and
 * interpolating it straight into a sentence printed "Buy 1.000 x Frozen Matar".
 * The kg path was never affected because it already went through Number() on
 * its way into amountText; the item path interpolated the raw column.
 */
export function countText(v: number | string): string {
  return String(Number(v));
}

export function amountText(qty: number, unit: string): string {
  if (unit === 'kg' && qty > 0 && qty < 1) return `${Math.round(qty * 1000)} g`;
  // Trim a trailing .0 — "2 kg", never "2.0 kg".
  const n = Number.isInteger(qty) ? String(qty) : String(Number(qty.toFixed(3)));
  return `${n} ${unit}`;
}

/** The quantity that has to be bought, in whichever mode the offer counts in. */
export function buyText(offer: ProductOffer): string {
  return offer.buy_unit === 'items'
    ? countText(offer.buy_qty)
    : amountText(Number(offer.buy_qty), 'kg');
}

/**
 * What they get for it.
 *
 * `withName` is false when the reward is the same product the reader is already
 * looking at — "get 1 packet Frozen Matar free" under a Frozen Matar heading
 * says it twice.
 */
export function rewardText(offer: ProductOffer, withName = true): string {
  // "the same pack", because the size is whichever one they put in the basket —
  // naming a weight here would be naming the wrong one for somebody.
  if (offer.get_matches_variant) {
    const n = Number(offer.get_qty);
    const packs = n === 1 ? 'the same size free' : `${countText(n)} more of the same size free`;
    return withName ? `${packs} ${offer.get_name}`.replace(' free ', ' free, ') : packs;
  }
  const amount = amountText(Number(offer.get_qty), offer.get_unit);
  const what = withName ? `${amount} ${offer.get_name}` : amount;
  const price = Number(offer.get_price);
  return price > 0 ? `${what} for ₹${price}` : `${what} free`;
}

/** True when the offer gives back the very thing being bought. */
export function isSameProduct(offer: ProductOffer): boolean {
  return Number(offer.buy_product_id) === Number(offer.get_product_id);
}

/**
 * The offer in one line, for the card of the product that triggers it.
 *
 * It does not name the product being bought, on purpose. This sits on that
 * product's own card, beneath its own name and price, so repeating it spends
 * the line on what the reader already knows — and the line is clamped to two,
 * so what it spends it cannot show. A real one ran past the edge as
 * "Buy 1 × Frozen Matar (5 kg), get 1 packet Frozen Mat…".
 *
 * The reward keeps its name only when it is a different product, which is the
 * one case the reader cannot infer.
 *
 * Deliberately not the admin's `label`: that is a name for ops to recognise in
 * a table and may say anything at all. What a customer reads is derived from
 * the numbers that will actually be applied, so the promise and the rule behind
 * it cannot drift apart.
 */
export function offerText(offer: ProductOffer): string {
  return `Buy ${buyText(offer)}, get ${rewardText(offer, !isSameProduct(offer))}`;
}

/** Keyed by the product that triggers it, which is how every caller looks it up. */
export function byBuyProduct(offers: ProductOffer[]): Map<number, ProductOffer> {
  const map = new Map<number, ProductOffer>();
  // First wins: the feed arrives in the admin's own sort_order, so a pinned
  // offer stays the one advertised when a product carries two.
  for (const o of offers) if (!map.has(o.buy_product_id)) map.set(o.buy_product_id, o);
  return map;
}

/**
 * How close this cart is to earning the offer.
 *
 * `bought` is in the offer's own counting mode — kilos for a weight offer,
 * whole items for a piece offer — so the caller does the summing and this does
 * the arithmetic. The epsilon matches the server's: a customer who put two
 * kilos in the basket has bought two kilos, whatever the float says.
 */
export function offerProgress(offer: ProductOffer, bought: number):
  { earned: number; shortfall: number } {
  const need = Number(offer.buy_qty);
  if (!(need > 0)) return { earned: 0, shortfall: 0 };
  const earned = Math.min(Math.floor(bought / need + 1e-9), Number(offer.max_repeats) || 1);
  return {
    earned,
    // Only ever the gap to the NEXT one, so a cart that has earned its maximum
    // stops being nagged toward a reward it cannot have.
    shortfall: earned > 0 ? 0 : Math.max(0, need - bought),
  };
}

/**
 * What to tell the customer about that progress, or null when there is nothing
 * worth saying.
 *
 * The nudge is deliberately specific — "add 500 g more" rather than "spend more
 * to unlock rewards". A number someone can act on beats an invitation to go
 * and work it out.
 *
 * Like the card, this sits under the product it is about, so the reward is
 * named only when it is a different one.
 */
export function offerProgressText(offer: ProductOffer, bought: number): string | null {
  const { earned, shortfall } = offerProgress(offer, bought);
  const named = !isSameProduct(offer);
  if (earned > 0) {
    // A pack-matching reward is counted in packs. Saying "8 kg free" for eight
    // 500g packs names a weight nobody is getting — and names it four times too
    // large.
    const total = Number(offer.get_qty) * earned;
    const amount = offer.get_matches_variant
      ? `${countText(total)} free pack${total === 1 ? '' : 's'}`
      : amountText(total, offer.get_unit);
    const what = named && !offer.get_matches_variant ? `${amount} ${offer.get_name}` : amount;
    if (offer.get_matches_variant) {
      return Number(offer.get_price) > 0
        ? `Offer applied · ${what} at ₹${Number(offer.get_price) * earned}`
        : `Offer applied · ${what}`;          // "free" is already in the phrase
    }
    return Number(offer.get_price) > 0
      ? `Offer applied · ${what} at ₹${Number(offer.get_price) * earned}`
      : `Offer applied · ${what} free`;
  }
  if (shortfall <= 0) return null;
  const more = offer.buy_unit === 'items'
    ? `${countText(shortfall)} more`
    : `${amountText(shortfall, 'kg')} more`;
  return `Add ${more} to get ${rewardText(offer, named)}`;
}
